import React from "react";
import BehaviourBarGraph from "../BehaviourBarGraph.jsx";
import {
  formatMass,
  formatPercentage,
} from "@signolteam/shared/units-of-measure";

let defaultMetadata = {
  fuel_burn_reto_1_without_warmup: 100,
  fuel_burn_reto_2_without_warmup: 40,
  actual_taxi_out_engine1_fuel_burn_kg: 100, // (old) fuel_burn_taxi_out1
  actual_taxi_out_engine2_fuel_burn_kg: 40, // (old) fuel_burn_taxi_out2
  actual_taxi_out_duration_minutes: 12, // (old) taxi_out_minutes
  constant_reto_warmup_time: 3,
  constant_reto_threshold: 0.2,
};

export const behaviourMetadataFields = Object.keys(defaultMetadata);
export default function graph({
  bvrColour,
  unit = "kg",
  metadata = defaultMetadata,
  industry,
  missingDataMsg,
  isExample,
}) {
  let useWithoutWarmup =
    (Number(metadata["fuel_burn_reto_1_without_warmup"]) ||
      Number(metadata["fuel_burn_reto_2_without_warmup"])) > 0;
  let engine1Value = useWithoutWarmup
    ? Number(metadata["fuel_burn_reto_1_without_warmup"])
    : Number(metadata["actual_taxi_out_engine1_fuel_burn_kg"]);
  let engine2Value = useWithoutWarmup
    ? Number(metadata["fuel_burn_reto_2_without_warmup"])
    : Number(metadata["actual_taxi_out_engine2_fuel_burn_kg"]);
  let thresholdPercentage = 1 - metadata["constant_reto_threshold"];

  if (isNaN(engine1Value) || isNaN(engine2Value) || !thresholdPercentage) {
    return (
      <div className="relative text-center my-6 mx-auto text-2xl fill-current">
        {missingDataMsg}
      </div>
    );
  }

  engine1Value = Number(engine1Value);
  engine2Value = Number(engine2Value);

  let maxEngineValue =
    engine1Value > engine2Value ? engine1Value : engine2Value;
  let minEngineValue =
    engine1Value > engine2Value ? engine2Value : engine1Value;
  let thresholdValue = Number(maxEngineValue * thresholdPercentage);
  let successStatus = minEngineValue < thresholdValue;
  const primaryEngine = engine1Value >= engine2Value ? 1 : 2;

  const data = [
    {
      name: "Engine 1",
      mobileName: `Label 1`,
      beforeThreshold:
        engine1Value >= thresholdValue ? thresholdValue : engine1Value,
      afterThreshold:
        engine1Value >= thresholdValue ? engine1Value - thresholdValue : 0,
      underThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : bvrColour.codes["200"],
      overThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : primaryEngine === 1
          ? bvrColour.codes["200"]
          : bvrColour.codes["900"],
    },
    {
      name: "Engine 2",
      mobileName: `Label 2`,
      beforeThreshold:
        engine2Value > thresholdValue ? thresholdValue : engine2Value,
      afterThreshold:
        engine2Value > thresholdValue ? engine2Value - thresholdValue : 0,
      underThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : bvrColour.codes["200"],
      overThresholdColour: successStatus
        ? bvrColour.codes["500"]
        : primaryEngine === 2
          ? bvrColour.codes["200"]
          : bvrColour.codes["900"],
    },
  ];

  const domainMin = 0;

  const margin = 0.1 * maxEngineValue;
  const domainMax =
    maxEngineValue < thresholdValue
      ? thresholdValue + margin
      : Math.ceil((maxEngineValue + margin) / 10) * 10;
  const domain = [domainMin, domainMax];

  const mobileMargin = 0.04 * maxEngineValue;
  const mobileDomainMax =
    maxEngineValue < thresholdValue
      ? thresholdValue + mobileMargin
      : Math.ceil(maxEngineValue + mobileMargin);
  const mobileDomain = [domainMin, mobileDomainMax];
  const thresholdLineColour = bvrColour.codes["900"];

  return (
    <BehaviourBarGraph
      data={data}
      threshold={thresholdValue}
      thresholdLabel={`Signol threshold${
        !isExample
          ? ` (${formatPercentage("0-100", thresholdPercentage * 100).formattedValue})`
          : ""
      }`}
      mobileDomain={mobileDomain}
      domain={domain}
      refLineColour={thresholdLineColour}
      success={successStatus}
      unitOfMeasure={unit}
      axisLabel={`Fuel consumption before warm up time`}
      hideYLine={true}
      hideNum={true}
      barLabelFormatter={(value) =>
        `${formatMass(industry, value, 0, { boundaryValues: [0, thresholdValue] }).formattedValue}`
      }
      isExample={isExample}
    />
  );
}
